import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { BarChart3, TrendingUp, Users, Calendar, Home, Download, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const Analytics = () => {
  const [analytics, setAnalytics] = useState({
    overview: {},
    propertyStats: [],
    appointmentStats: [],
    communeStats: [],
    monthlyTrends: []
  });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // Last 30 days by default

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      const currentDate = new Date();
      const startDate = new Date();
      startDate?.setDate(currentDate?.getDate() - parseInt(dateRange));

      // Fetch overview statistics
      const [usersRes, propertiesRes, appointmentsRes, inquiriesRes] = await Promise.all([
        supabase?.from('user_profiles')?.select('id, role', { count: 'exact' }),
        supabase?.from('properties')?.select('id, status, property_type, price', { count: 'exact' }),
        supabase?.from('appointments')?.select('id, status, scheduled_date')?.gte('created_at', startDate?.toISOString()),
        supabase?.from('property_inquiries')?.select('id, status')?.gte('created_at', startDate?.toISOString())
      ]);

      // Process overview data
      const totalUsers = usersRes?.count || 0;
      const usersByRole = usersRes?.data?.reduce((acc, user) => {
        acc[user?.role] = (acc?.[user?.role] || 0) + 1;
        return acc;
      }, {});

      const totalProperties = propertiesRes?.count || 0;
      const propertiesByStatus = propertiesRes?.data?.reduce((acc, property) => {
        acc[property?.status] = (acc?.[property?.status] || 0) + 1;
        return acc;
      }, {});

      const propertiesByType = propertiesRes?.data?.reduce((acc, property) => {
        acc[property?.property_type] = (acc?.[property?.property_type] || 0) + 1;
        return acc;
      }, {});

      const totalAppointments = appointmentsRes?.data?.length || 0;
      const appointmentsByStatus = appointmentsRes?.data?.reduce((acc, appointment) => {
        acc[appointment?.status] = (acc?.[appointment?.status] || 0) + 1;
        return acc;
      }, {});

      // Average property price
      const avgPrice = propertiesRes?.data?.length > 0 
        ? propertiesRes?.data?.reduce((sum, p) => sum + (p?.price || 0), 0) / propertiesRes?.data?.length 
        : 0;

      // Fetch commune statistics
      const { data: communeData } = await supabase?.from('properties')?.select(`
          commune_id,
          commune:communes(name),
          price
        `)?.not('commune_id', 'is', null);

      const communeStats = communeData?.reduce((acc, property) => {
        const communeName = property?.commune?.name || 'Inconnu';
        if (!acc?.[communeName]) {
          acc[communeName] = { count: 0, totalValue: 0 };
        }
        acc[communeName].count += 1;
        acc[communeName].totalValue += property?.price || 0;
        return acc;
      }, {});

      // Fetch monthly trends
      const { data: monthlyData } = await supabase?.from('appointments')?.select('created_at, status')?.gte('created_at', new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000)?.toISOString())?.order('created_at');

      const monthlyTrends = monthlyData?.reduce((acc, appointment) => {
        const month = new Date(appointment?.created_at)?.toLocaleDateString('fr-FR', { 
          year: 'numeric', 
          month: 'short' 
        });
        if (!acc?.[month]) {
          acc[month] = { month, appointments: 0, completed: 0, cancelled: 0 };
        }
        acc[month].appointments += 1;
        if (appointment?.status === 'completed') {
          acc[month].completed += 1;
        }
        if (appointment?.status === 'cancelled') {
          acc[month].cancelled += 1;
        }
        return acc;
      }, {});

      setAnalytics({
        overview: {
          totalUsers,
          totalProperties,
          totalAppointments,
          totalInquiries: inquiriesRes?.data?.length || 0,
          avgPrice,
          usersByRole,
          propertiesByStatus,
          propertiesByType,
          appointmentsByStatus
        },
        propertyStats: Object.entries(propertiesByType)?.map(([type, count]) => ({
          name: type === 'apartment' ? 'Appartement' : 
                type === 'house' ? 'Maison' :
                type === 'villa' ? 'Villa' :
                type === 'land' ? 'Terrain' :
                type === 'commercial' ? 'Commercial' : type,
          value: count,
          percentage: ((count / totalProperties) * 100)?.toFixed(1)
        })),
        appointmentStats: Object.entries(appointmentsByStatus)?.map(([status, count]) => ({
          name: status === 'scheduled' ? 'Programmé' :
                status === 'confirmed' ? 'Confirmé' :
                status === 'completed' ? 'Terminé' :
                status === 'cancelled' ? 'Annulé' : status,
          value: count
        })),
        communeStats: Object.entries(communeStats || {})?.map(([name, data]) => ({
          name,
          properties: data?.count,
          avgPrice: data?.count > 0 ? (data?.totalValue / data?.count) : 0
        }))?.slice(0, 10),
        monthlyTrends: Object.values(monthlyTrends || {})?.slice(-6)
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    })?.format(value);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white p-8 rounded-lg shadow-sm border text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <BarChart3 className="h-6 w-6 mr-2" />
            Analyses & Rapports
          </h2>
          <p className="text-sm text-gray-600">
            Insights détaillés sur les comportements utilisateurs, taux de conversion et utilisation des services
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7">7 derniers jours</option>
            <option value="30">30 derniers jours</option>
            <option value="90">90 derniers jours</option>
            <option value="365">1 an</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Actualiser</span>
          </button>
        </div>
      </div>
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Utilisateurs Total</p>
              <p className="text-2xl font-bold text-gray-900">{analytics?.overview?.totalUsers}</p>
            </div>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Admins: {analytics?.overview?.usersByRole?.admin || 0} | 
            Agents: {analytics?.overview?.usersByRole?.agent || 0} | 
            Clients: {analytics?.overview?.usersByRole?.client || 0}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Propriétés</p>
              <p className="text-2xl font-bold text-gray-900">{analytics?.overview?.totalProperties}</p>
            </div>
            <Home className="h-8 w-8 text-green-500" />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Disponibles: {analytics?.overview?.propertiesByStatus?.available || 0} | 
            Vendues: {analytics?.overview?.propertiesByStatus?.sold || 0}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rendez-vous</p>
              <p className="text-2xl font-bold text-gray-900">{analytics?.overview?.totalAppointments}</p>
            </div>
            <Calendar className="h-8 w-8 text-purple-500" />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Terminés: {analytics?.overview?.appointmentsByStatus?.completed || 0} | 
            Annulés: {analytics?.overview?.appointmentsByStatus?.cancelled || 0}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Prix Moyen</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency(analytics?.overview?.avgPrice)}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-orange-500" />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Demandes: {analytics?.overview?.totalInquiries}
          </div>
        </div>
      </div>
      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Property Types Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Type de Propriété</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics?.propertyStats}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percentage }) => `${name}: ${percentage}%`}
              >
                {analytics?.propertyStats?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Appointment Status Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Statut des Rendez-vous</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics?.appointmentStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Commune Statistics */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 10 Communes par Propriétés</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analytics?.communeStats} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={80} />
              <Tooltip formatter={(value, name) => [
                name === 'properties' ? `${value} propriétés` : formatCurrency(value),
                name === 'properties' ? 'Propriétés' : 'Prix moyen'
              ]} />
              <Bar dataKey="properties" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tendances Mensuelles</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics?.monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="appointments" stroke="#8884d8" name="RDV Total" />
              <Line type="monotone" dataKey="completed" stroke="#82ca9d" name="Terminés" />
              <Line type="monotone" dataKey="cancelled" stroke="#ff7c7c" name="Annulés" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Property Performance */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Performance des Propriétés</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics?.propertyStats?.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item?.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item?.value}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item?.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Commune Performance */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Performance par Commune</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commune</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Propriétés</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix Moyen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics?.communeStats?.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item?.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item?.properties}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatCurrency(item?.avgPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Export Options */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Options d'Export</h3>
          <div className="flex space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <Download className="h-4 w-4" />
              <span>Export PDF</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;